import React, { useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import Select from "react-select";
import { SELECT_STYLES } from "../../utils/selectStyles";
import { CButton, CCollapse, CFormInput, CFormSelect } from "@coreui/react";
import { Tooltip } from "react-tooltip";
import * as Sentry from "@sentry/react";
import DashboardContent from "../../components/DashboardContent";
import { usePermissions } from "../../contexts/PermissionsContext";
import { PERMISSIONS } from "../../utils/permissions";
import RedirectHandler from "../../components/RedirectHandler";
import useCsr from "../../hooks/useCsr";
import { CsrAPI } from "../../apis/CsrAPI";
import QuestionItemInfo from "../../components/QuestionItemInfo";
import { ReactComponent as BinIcon } from "../../assets/images/icons/bin.svg";
import { ReactComponent as PlusIcon } from "../../assets/images/icons/plus.svg";
import { ReactComponent as CopyIcon } from "../../assets/images/icons/copy-white.svg";
import { ReactComponent as CopyOutlineIcon } from "../../assets/images/icons/copy.svg";
import {
  SCS_COMPARISON_TYPE_OPTIONS,
  SCS_TREATMENT_GROUP_ROLE_OPTIONS,
} from "../../utils/scsDescriptionOfStudiesOptions";
import {
  SCS_AAE_COMPARISON_TYPE_LABELS,
  SCS_AAE_COMPARISON_TYPE_VALUES,
  SCS_AAE_QUESTIONS,
  SCS_AE_TYPE_OPTIONS,
} from "../../utils/scsAnalysisOfAdverseEventsQuestions";
import { emptyWhenUnanswered } from "../../utils/scsWizardPayload";

const isProduction = process.env.NODE_ENV === "production";

const FORM_ID = "scs-analysis-of-adverse-events-form";
const REMOVE_TOOLTIP_ID = "scs-aae-remove";
const DUPLICATE_TOOLTIP_ID = "scs-aae-duplicate";

// Repeatable blocks (the table/analysis sections and the additional groupings
// inside them) always keep at least one entry; the first entry is non-removable
// (mirrors the Description of Studies / Overall Extent of Exposure pattern).
const MIN_BLOCK_ENTRIES = 1;

const emptyTreatmentGroup = () => ({ name: "", role: "" });
const emptyComparisonPair = () => ({ treatment: "", comparator: "" });

// UI shape of one additional grouping (Q2–Q5) inside a table/analysis section.
// Like the Description of Studies comparison blocks it holds every branch
// answer, so switching the comparison type (Q3) never loses what was already
// typed; only the answers belonging to the selected type are persisted (see
// buildGroupingPayload). Q3 is held as the on-screen wording and only becomes
// its saved value (single_arm / multi_arm / dose_finding) in the payload.
const emptyGrouping = () => ({
  qAEAdditionalGroupingName: "",
  qAEAdditionalGroupingStudies: [],
  qAEComparisonType: "",
  qAENonComparativeGroupNames: [""],
  qAETreatmentGroups: [emptyTreatmentGroup()],
  qAEComparisonPairs: [emptyComparisonPair()],
  qAEDoseGroups: [""],
});

// UI shape of one table/analysis section (the whole Q1–Q5 flow). This page is
// organised around these sections only — with no AE category picked in Q1 the
// section reports nothing, so nothing below it is shown.
const emptyAnalysis = () => ({
  qAETypes: [],
  groupings: [emptyGrouping()],
});

const seedList = list =>
  Array.isArray(list) && list.length > 0 ? [...list] : [""];

// Treatment group roles are saved lowercase ("treatment" / "comparator") but
// shown with the wording of SCS_TREATMENT_GROUP_ROLE_OPTIONS on screen — same
// convention as the Description of Studies page.
const roleForPayload = role => (role || "").toLowerCase();
const roleForDisplay = role =>
  SCS_TREATMENT_GROUP_ROLE_OPTIONS.find(
    option => option.toLowerCase() === (role || "").toLowerCase()
  ) || "";

// Q1 answers (and so the qAETypes entries) are the category wording, not the
// category keys — see SCS_AE_TYPE_OPTIONS.
const AE_TYPE_LABELS = SCS_AE_TYPE_OPTIONS.map(option => option.label);
const AE_TYPE_LABEL_SET = new Set(AE_TYPE_LABELS);
const AE_TYPE_LABEL_BY_KEY = new Map(
  SCS_AE_TYPE_OPTIONS.map(option => [option.key, option.label])
);

// Accept a category held as its label or (defensively) as its key.
const asAeTypeLabel = value =>
  AE_TYPE_LABEL_SET.has(value) ? value : AE_TYPE_LABEL_BY_KEY.get(value);

// Q1 answers are saved as the category labels; keep them in the page order so
// they never jump around as categories are picked.
const orderedAeTypes = types => {
  const selected = new Set(
    (Array.isArray(types) ? types : Object.keys(types || {}))
      .map(asAeTypeLabel)
      .filter(Boolean)
  );
  return AE_TYPE_LABELS.filter(label => selected.has(label));
};

// Deep-clone an additional grouping / table-analysis section already in its UI
// shape (the "Duplicate" controls), so the copy is fully independent from its
// source (mutating one never affects the other).
const cloneGrouping = grouping => ({
  ...emptyGrouping(),
  qAEAdditionalGroupingName: grouping?.qAEAdditionalGroupingName || "",
  qAEAdditionalGroupingStudies: Array.isArray(
    grouping?.qAEAdditionalGroupingStudies
  )
    ? [...grouping.qAEAdditionalGroupingStudies]
    : [],
  qAEComparisonType: grouping?.qAEComparisonType || "",
  qAENonComparativeGroupNames: seedList(grouping?.qAENonComparativeGroupNames),
  qAETreatmentGroups: (grouping?.qAETreatmentGroups?.length
    ? grouping.qAETreatmentGroups
    : [emptyTreatmentGroup()]
  ).map(tg => ({ name: tg?.name || "", role: roleForDisplay(tg?.role) })),
  qAEComparisonPairs: (grouping?.qAEComparisonPairs?.length
    ? grouping.qAEComparisonPairs
    : [emptyComparisonPair()]
  ).map(pair => ({
    treatment: pair?.treatment || "",
    comparator: pair?.comparator || "",
  })),
  qAEDoseGroups: seedList(grouping?.qAEDoseGroups),
});

// --- Saved -> UI shape for the additional groupings -----------------------
//
// A saved grouping carries the comparison type it was answered with and only
// that type's branch answers (see SCS_AAE_COMPARISON_TYPE_VALUES):
//   single_arm   qAETreatmentGroups: ["TG1", …]
//   multi_arm    qAETreatmentGroups: [{ name, role }, …]
//                qAEComparisonPairs: [{ treatment, comparator }, …]
//   dose_finding qAEDoseGroups: ["TG7", …]
// The single_arm names are saved under the same attribute as the multi_arm
// treatment groups but belong to the Non-Comparative question on screen, so
// they are put back on that one; Q3 goes back to its on-screen wording.
const restoreGrouping = saved => {
  const comparisonType =
    SCS_AAE_COMPARISON_TYPE_LABELS[saved?.qAEComparisonType] || "";
  const treatmentGroups = Array.isArray(saved?.qAETreatmentGroups)
    ? saved.qAETreatmentGroups
    : [];
  // Both branches tolerate either spelling of an entry ("TG1" / { name: "TG1" }).
  const asName = tg => (typeof tg === "string" ? tg : tg?.name || "");
  return cloneGrouping({
    ...saved,
    qAEComparisonType: comparisonType,
    qAENonComparativeGroupNames:
      comparisonType === "Non-Comparative"
        ? treatmentGroups.map(asName)
        : undefined,
    qAETreatmentGroups:
      comparisonType === "Comparative"
        ? treatmentGroups.map(tg => ({
            name: asName(tg),
            role: typeof tg === "string" ? "" : tg?.role,
          }))
        : undefined,
  });
};

// Rebuild a table/analysis section from saved data.
const restoreAnalysis = analysis => ({
  ...emptyAnalysis(),
  qAETypes: orderedAeTypes(analysis?.qAETypes),
  groupings: (analysis?.groupings?.length
    ? analysis.groupings
    : [emptyGrouping()]
  ).map(restoreGrouping),
});

// Deep-clone a table/analysis section already in its UI shape (the "Duplicate"
// controls), so the copy is fully independent from its source.
const cloneAnalysisBlock = analysis => ({
  ...emptyAnalysis(),
  qAETypes: orderedAeTypes(analysis?.qAETypes),
  groupings: (analysis?.groupings?.length
    ? analysis.groupings
    : [emptyGrouping()]
  ).map(cloneGrouping),
});

// The saved answers arrive either bare or wrapped, and as a list of sections or
// as a single section; collapse every shape to a plain list of sections.
const savedAnalysesFrom = payload => {
  const section = payload?.analysis_of_adverse_events || payload;
  if (!section || typeof section !== "object") return [];
  if (Array.isArray(section)) return section.filter(Boolean);
  const analyses = section.analyses;
  if (Array.isArray(analyses)) return analyses.filter(Boolean);
  if (analyses && typeof analyses === "object") return [analyses];
  return [];
};

const buildAnalyses = saved => {
  const blocks = (saved || []).map(restoreAnalysis);
  return blocks.length > 0 ? blocks : [emptyAnalysis()];
};

// Repeatable lists always keep at least one entry; the first is non-removable.
function baseGroupNames(row) {
  return (row.qAENonComparativeGroupNames || []).length > 0
    ? row.qAENonComparativeGroupNames
    : [""];
}
function baseTreatmentGroups(row) {
  return (row.qAETreatmentGroups || []).length > 0
    ? row.qAETreatmentGroups
    : [emptyTreatmentGroup()];
}
function basePairs(row) {
  return (row.qAEComparisonPairs || []).length > 0
    ? row.qAEComparisonPairs
    : [emptyComparisonPair()];
}
function baseDoseGroups(row) {
  return (row.qAEDoseGroups || []).length > 0 ? row.qAEDoseGroups : [""];
}

const toOptions = values => (values || []).map(v => ({ value: v, label: v }));
const fromOptions = options => (options || []).map(option => option.value);

const SCSAnalysisOfAdverseEvents = () => {
  const { studyId: summaryId } = useParams();
  const { setAjaxLoading, wrongEnvironment } = useCsr();
  const navigate = useNavigate();
  const { hasPermission } = usePermissions();

  // scs:wizards:read gates the GET that pre-populates this page; writing it
  // back needs scs:wizards:update on top, so without either the Save button is
  // disabled and Next navigates without submitting.
  const canReadWizards = hasPermission(PERMISSIONS.scsWizardsRead);
  const canSaveWizards =
    canReadWizards && hasPermission(PERMISSIONS.scsWizardsUpdate);

  const [studyNameOptions, setStudyNameOptions] = useState([]);
  const [questionText, setQuestionText] = useState({});
  // One entry per table/analysis section — the only repeatable unit this page
  // is organised around.
  const [analyses, setAnalyses] = useState([emptyAnalysis()]);
  // Which table/analysis accordion items are open (by array index). The first
  // is expanded when the page loads; a newly added / duplicated section (always
  // the last) becomes the only expanded one.
  const [openAnalysisIndexes, setOpenAnalysisIndexes] = useState(
    () => new Set([0])
  );

  // Q1 is required per table/analysis section: { [analysisIndex]: message }.
  const [analysisErrors, setAnalysisErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showRedirect, setShowRedirect] = useState(false);

  // Table/analysis blocks, so a newly added one — or the one holding the first
  // validation error — can be scrolled into view.
  const analysisRefs = useRef({});
  const pendingScrollRef = useRef(null);

  const mountedRef = useRef(true);
  useEffect(() => {
    return () => {
      mountedRef.current = false;
    };
  }, []);

  // Study name options come from the same endpoint the Description of Studies
  // page uses for its Q2A study dropdowns.
  useEffect(() => {
    CsrAPI.getAllStudiesForLaySummaries()
      .then(allStudies => {
        if (!mountedRef.current) return;
        const names = (allStudies || [])
          .map(study => study?.name)
          .filter(Boolean);
        setStudyNameOptions([...new Set(names)]);
      })
      .catch(error => {
        if (isProduction) Sentry.captureException(error);
      });
  }, []);

  // Question wording is served from the shared safety-summary questions
  // endpoint; fall back to the bundled constants when it is unavailable.
  useEffect(() => {
    CsrAPI.getSafetySummaryQuestions()
      .then(data => {
        if (mountedRef.current && data) setQuestionText(data);
      })
      .catch(error => {
        if (isProduction) Sentry.captureException(error);
      });
  }, []);

  // Pre-populate previously saved wizard state. Only the first section is left
  // expanded once the saved answers are restored.
  useEffect(() => {
    if (!summaryId) return;
    // Without scs:wizards:read the endpoint is never called; the page starts
    // from its empty form instead.
    if (!canReadWizards) return;

    setAjaxLoading(true);
    CsrAPI.getSafetySummaryAnalysisOfAdverseEvents({ summaryId })
      .then(data => {
        if (!mountedRef.current) return;
        setAnalyses(buildAnalyses(savedAnalysesFrom(data)));
        setOpenAnalysisIndexes(new Set([0]));
      })
      .catch(error => {
        if (!mountedRef.current) return;
        // A 404 simply means nothing has been saved yet — start with defaults.
        if (error?.response?.status === 404) return;
        if (isProduction) Sentry.captureException(error);
        if (error?.response?.status === 403) {
          setShowRedirect(true);
        } else {
          toast.error("Failed to load previously saved data.");
        }
      })
      .finally(() => {
        if (mountedRef.current) setAjaxLoading(false);
      });
  }, [summaryId, setAjaxLoading, canReadWizards]);

  // Scroll a newly added / duplicated table/analysis section — or the one
  // holding the first validation error — into view. Walk up to the named scroll
  // container so we never scroll the browser viewport (matches the Description
  // of Studies block behaviour).
  useEffect(() => {
    const index = pendingScrollRef.current;
    if (index === null || index === undefined) return;
    pendingScrollRef.current = null;
    const el = analysisRefs.current[index];
    if (!el) return;
    let wrapper = el.parentElement;
    while (
      wrapper &&
      !wrapper.classList.contains("dashboard-content__wrapper")
    ) {
      wrapper = wrapper.parentElement;
    }
    if (wrapper) {
      const elTop = el.getBoundingClientRect().top;
      const wrapperTop = wrapper.getBoundingClientRect().top;
      wrapper.scrollBy({ top: elTop - wrapperTop, behavior: "smooth" });
    }
  });

  // --- Accordion open/closed state ----------------------------------------
  const toggleAnalysis = index =>
    setOpenAnalysisIndexes(prev => {
      const next = new Set(prev);
      next.has(index) ? next.delete(index) : next.add(index);
      return next;
    });

  // --- Table/analysis and additional grouping mutators --------------------
  const updateAnalysis = (analysisIndex, updater) =>
    setAnalyses(prev =>
      prev.map((row, i) => (i === analysisIndex ? updater(row) : row))
    );

  const updateGrouping = (analysisIndex, groupingIndex, updater) =>
    updateAnalysis(analysisIndex, analysis => ({
      ...analysis,
      groupings: (analysis.groupings || []).map((row, i) =>
        i === groupingIndex ? updater(row) : row
      ),
    }));

  // "Add another table/analysis" appends an empty section, "Duplicate another
  // table/analysis" appends a copy of the last one and the per-section
  // "Duplicate" pill appends a copy of that section. New sections always go to
  // the end and become the only expanded accordion item.
  const appendAnalysis = factory => {
    const added = factory(analyses);
    if (!added) return;
    setAnalyses(prev => [...prev, added]);
    pendingScrollRef.current = analyses.length;
    setOpenAnalysisIndexes(new Set([analyses.length]));
  };

  const addAnalysis = () => appendAnalysis(() => emptyAnalysis());
  const duplicateAnalysis = analysisIndex =>
    appendAnalysis(list =>
      list[analysisIndex] ? cloneAnalysisBlock(list[analysisIndex]) : null
    );
  const duplicateAnalysisAbove = () =>
    appendAnalysis(list =>
      list.length ? cloneAnalysisBlock(list[list.length - 1]) : null
    );
  const removeAnalysis = analysisIndex => {
    if (analyses.length <= MIN_BLOCK_ENTRIES) return;
    setAnalyses(prev => prev.filter((_, i) => i !== analysisIndex));
    // The remaining sections shift down a place, so drop the stale open indexes
    // and leave the last remaining section expanded.
    setOpenAnalysisIndexes(new Set([analyses.length - 2]));
    clearError(analysisIndex);
  };

  // "Add another group" / "Duplicate group above" repeat Q2–Q5 inside one
  // table/analysis section.
  const addGrouping = analysisIndex =>
    updateAnalysis(analysisIndex, analysis => ({
      ...analysis,
      groupings: [...(analysis.groupings || []), emptyGrouping()],
    }));
  const duplicateGrouping = (analysisIndex, groupingIndex) =>
    updateAnalysis(analysisIndex, analysis => {
      const groupings = analysis.groupings || [];
      if (!groupings[groupingIndex]) return analysis;
      return {
        ...analysis,
        groupings: [...groupings, cloneGrouping(groupings[groupingIndex])],
      };
    });
  const duplicateGroupingAbove = analysisIndex =>
    updateAnalysis(analysisIndex, analysis => {
      const groupings = analysis.groupings || [];
      if (!groupings.length) return analysis;
      return {
        ...analysis,
        groupings: [
          ...groupings,
          cloneGrouping(groupings[groupings.length - 1]),
        ],
      };
    });
  const removeGrouping = (analysisIndex, groupingIndex) =>
    updateAnalysis(analysisIndex, analysis => {
      const groupings = analysis.groupings || [];
      if (groupings.length <= MIN_BLOCK_ENTRIES) return analysis;
      return {
        ...analysis,
        groupings: groupings.filter((_, i) => i !== groupingIndex),
      };
    });

  const clearError = analysisIndex =>
    setAnalysisErrors(prev => {
      if (prev[analysisIndex] === undefined) return prev;
      const next = { ...prev };
      delete next[analysisIndex];
      return next;
    });

  // Q1 — the picked AE categories are the whole answer; no per-category
  // follow-up question is asked.
  const setAeTypes = (analysisIndex, labels) => {
    updateAnalysis(analysisIndex, analysis => ({
      ...analysis,
      qAETypes: orderedAeTypes(labels),
    }));
    if ((labels || []).length) clearError(analysisIndex);
  };

  // Q1 needs at least one AE category in every table/analysis section — a
  // section that reports no AEs has nothing to say.
  const validate = () => {
    const errors = {};
    // analyses.forEach((analysis, index) => {
    //   if (orderedAeTypes(analysis.qAETypes).length > 0) return;
    //   errors[index] = "Select at least one AE type";
    // });
    // setAnalysisErrors(errors);

    const firstInvalid = Object.keys(errors)[0];
    if (firstInvalid !== undefined) {
      const index = Number(firstInvalid);
      // Expand the offending section before scrolling to it, otherwise the
      // question is hidden inside a collapsed accordion item.
      setOpenAnalysisIndexes(prev => new Set(prev).add(index));
      pendingScrollRef.current = index;
      return false;
    }
    return true;
  };

  // Only the answers belonging to the comparison type picked in Q3 are
  // persisted, under the attributes of the kind of grouping that type is saved
  // as:
  //   single_arm   -> qAETreatmentGroups (the plain group names)
  //   multi_arm    -> qAETreatmentGroups ({ name, role }) and
  //                   qAEComparisonPairs
  //   dose_finding -> qAEDoseGroups
  // With Q3 unanswered only the common answers are saved, so nothing typed
  // into a branch is written under a comparison type the user did not pick.
  const buildGroupingPayload = grouping => {
    const common = {
      qAEAdditionalGroupingName: grouping.qAEAdditionalGroupingName || "",
      qAEAdditionalGroupingStudies: grouping.qAEAdditionalGroupingStudies || [],
    };
    const comparisonType =
      SCS_AAE_COMPARISON_TYPE_VALUES[grouping.qAEComparisonType];
    if (comparisonType === "single_arm") {
      return {
        ...common,
        qAEComparisonType: comparisonType,
        // The Non-Comparative names have no role question on screen.
        qAETreatmentGroups: baseGroupNames(grouping)
          .map(name => (name || "").trim())
          .filter(Boolean),
      };
    }
    if (comparisonType === "multi_arm") {
      return {
        ...common,
        qAEComparisonType: comparisonType,
        qAETreatmentGroups: baseTreatmentGroups(grouping)
          .filter(tg => (tg?.name || "").trim())
          .map(tg => ({ name: tg.name, role: roleForPayload(tg.role) })),
        qAEComparisonPairs: basePairs(grouping).filter(
          pair => pair?.treatment || pair?.comparator
        ),
      };
    }
    if (comparisonType === "dose_finding") {
      return {
        ...common,
        qAEComparisonType: comparisonType,
        qAEDoseGroups: baseDoseGroups(grouping)
          .map(dose => (dose || "").trim())
          .filter(Boolean),
      };
    }
    return common;
  };

  const buildAnalysisPayload = analysis => ({
    qAETypes: orderedAeTypes(analysis.qAETypes),
    groupings: (analysis.groupings || []).map(buildGroupingPayload),
  });

  // Q3 is a button group with no way to un-pick the comparison type, so a
  // payload carrying nothing but comparison types is a page the user emptied:
  // it is saved as {} rather than as a section of blank answers.
  const buildPayload = () =>
    emptyWhenUnanswered({ analyses: analyses.map(buildAnalysisPayload) }, [
      "qAEComparisonType",
    ]);

  // Persists the wizard state via PATCH .../wizards/analysis_of_adverse_events.
  // Shared by the "Save" button (stays on the page) and the "Clinical
  // Laboratory Evaluations" button (navigates on success); returns whether the
  // save succeeded so the caller can decide what to do next.
  const saveWizardState = async () => {
    if (isSubmitting) return false;
    if (!canSaveWizards) return false;
    if (!validate()) {
      toast.error("Please fill in all required fields.");
      return false;
    }

    setIsSubmitting(true);
    try {
      await CsrAPI.saveSafetySummaryAnalysisOfAdverseEvents({
        summaryId,
        state: buildPayload(),
      });
      return true;
    } catch (error) {
      if (isProduction) Sentry.captureException(error);
      if (mountedRef.current) {
        if (error?.response?.status === 403) {
          setShowRedirect(true);
        } else {
          toast.error("Failed to save. Please try again.");
        }
      }
      return false;
    } finally {
      if (mountedRef.current) setIsSubmitting(false);
    }
  };

  const handleSubmit = async event => {
    event.preventDefault();
    if (!canSaveWizards) {
      navigate(`/scs/${summaryId}/clinical-laboratory-evaluations`);
      return;
    }

    const saved = await saveWizardState();
    if (saved && mountedRef.current) {
      navigate(`/scs/${summaryId}/clinical-laboratory-evaluations`);
    }
  };

  const handleSave = async event => {
    event?.preventDefault();
    const saved = await saveWizardState();
    if (saved && mountedRef.current) {
      toast.success("Data saved successfully!");
    }
  };

  const stringSelectOptions = values => [
    { value: "", label: "Select..." },
    ...values.map(v => ({ value: v, label: v })),
  ];

  const removeButton = (label, onClick) => (
    <CButton
      color={"danger"}
      shape={"pill"}
      aria-label={label}
      data-tooltip-id={REMOVE_TOOLTIP_ID}
      data-tooltip-content={label}
      onClick={onClick}>
      <BinIcon />
    </CButton>
  );

  const addButton = (label, onClick) => (
    <CButton
      color={"primary"}
      variant={"outline"}
      className={"btn-light scs-dos__add"}
      onClick={onClick}>
      {label} <PlusIcon />
    </CButton>
  );

  // Solid-blue pill sitting at the top of each block. A section holds both a
  // table/analysis and a grouping control, so each carries its own label.
  const duplicateBlockButton = (label, onClick) => (
    <CButton
      color={"primary"}
      shape={"pill"}
      className={"scs-dos__duplicate"}
      aria-label={label}
      data-tooltip-id={DUPLICATE_TOOLTIP_ID}
      data-tooltip-content={label}
      onClick={onClick}>
      <CopyIcon /> Duplicate
    </CButton>
  );

  // Outlined pill pairing with the matching "Add another …" control.
  const duplicateAboveButton = (label, onClick) => (
    <CButton
      color={"primary"}
      variant={"outline"}
      className={"btn-light scs-dos__add"}
      onClick={onClick}>
      {label} <CopyOutlineIcon />
    </CButton>
  );

  // Endpoint wording wins; fall back to the bundled question text.
  const qText = key => {
    const question = SCS_AAE_QUESTIONS[key];
    const served = [key, ...(question?.textKeys || [])].find(
      textKey => questionText[textKey]
    );
    return (served && questionText[served]) || question?.text || "";
  };

  // CSR-style numbered question: index badge in the left gutter, label +
  // control in the item column (mirrors QuestionItem).
  const renderQuestion = (key, control, numberOverride) => {
    const question = SCS_AAE_QUESTIONS[key];
    if (!question) return null;
    return (
      <div className={"questions-content__container"}>
        <div className={"questions-content__questionIndexContainer"}>
          <span className={"questions-content__label-index"}>
            {numberOverride ?? question.number}
          </span>
        </div>
        <div className={"questions-content__item"}>
          <div className={"questions-content__label"}>
            <span className={"questions-content__label-text"}>
              {qText(key)}
              {question.required && (
                <span className={"questions-content__required-asterisk"}>
                  {" "}
                  *
                </span>
              )}
            </span>
            {question.tooltip && (
              <QuestionItemInfo
                questionName={key}
                notes={{ [key]: question.tooltip }}
              />
            )}
          </div>
          <div className={"questions-content__input"}>{control}</div>
        </div>
      </div>
    );
  };

  // Q1 — multi-select of the AE categories. The categories themselves are the
  // whole answer; with nothing selected, nothing below it is shown.
  const renderAeTypes = (analysis, a, number) => {
    const error = analysisErrors[a];
    return renderQuestion(
      "qAETypes",
      <>
        <Select
          styles={SELECT_STYLES}
          isMulti
          className={"scs-dos__control"}
          inputId={`ae-types-${a}`}
          options={toOptions(AE_TYPE_LABELS)}
          value={toOptions(orderedAeTypes(analysis.qAETypes))}
          onChange={options => setAeTypes(a, fromOptions(options))}
          placeholder={"Select AE types"}
        />
        {error && <span className={"errmsg"}>{error}</span>}
      </>,
      number
    );
  };

  // Q2 — free-text grouping name plus the studies in that grouping (the same
  // pair of controls as Q2A of the Description of Studies page).
  const renderAdditionalGrouping = (grouping, a, i, number) =>
    renderQuestion(
      "qAEAdditionalGroupingName",
      <div className={"questions-content__input-group"}>
        <div className={"scs-dos__stacked-fields"}>
          <div className={"form-component"}>
            <label htmlFor={`aae-grouping-name-${a}-${i}`}>Grouping Name</label>
            <CFormInput
              id={`aae-grouping-name-${a}-${i}`}
              value={grouping.qAEAdditionalGroupingName || ""}
              placeholder={"e.g. Non-Comp(Total)"}
              onChange={e =>
                updateGrouping(a, i, row => ({
                  ...row,
                  qAEAdditionalGroupingName: e.target.value,
                }))
              }
            />
          </div>
          <div className={"form-component"}>
            <label>Studies</label>
            <Select
              styles={SELECT_STYLES}
              isMulti
              className={"scs-dos__control"}
              inputId={`aae-grouping-studies-${a}-${i}`}
              options={toOptions(studyNameOptions)}
              value={toOptions(grouping.qAEAdditionalGroupingStudies)}
              onChange={options =>
                updateGrouping(a, i, row => ({
                  ...row,
                  qAEAdditionalGroupingStudies: fromOptions(options),
                }))
              }
              placeholder={"Select studies"}
            />
          </div>
        </div>
        <span className={"empty-space-layout-helper"}></span>
      </div>,
      number
    );

  // Q3 — comparison type, the same single-select button group as Q7 of the
  // Description of Studies page.
  const renderComparisonType = (grouping, a, i, number) =>
    renderQuestion(
      "qAEComparisonType",
      SCS_COMPARISON_TYPE_OPTIONS.map(option => (
        <React.Fragment key={option}>
          <input
            type={"radio"}
            className={"btn-check"}
            name={`comparison-type-${a}-${i}`}
            id={`comparison-type-${a}-${i}-${option}`}
            autoComplete={"off"}
            checked={(grouping.qAEComparisonType || "") === option}
            onChange={() =>
              updateGrouping(a, i, row => ({
                ...row,
                qAEComparisonType: option,
              }))
            }
          />
          <label
            className={"btn btn-light"}
            htmlFor={`comparison-type-${a}-${i}-${option}`}>
            {option}
          </label>
        </React.Fragment>
      )),
      number
    );

  const renderNonComparativeGroupNames = (grouping, a, i, number) =>
    renderQuestion(
      "qAENonComparativeGroupNames",
      <>
        {baseGroupNames(grouping).map((groupName, gnIndex) => (
          <div
            key={`ncgn-${a}-${i}-${gnIndex}`}
            className={"questions-content__input-group"}>
            <div className={"form-component"}>
              <CFormInput
                value={groupName}
                placeholder={"Please enter treatment group name"}
                onChange={e =>
                  updateGrouping(a, i, row => ({
                    ...row,
                    qAENonComparativeGroupNames: baseGroupNames(row).map(
                      (name, index) =>
                        index === gnIndex ? e.target.value : name
                    ),
                  }))
                }
              />
            </div>
            {baseGroupNames(grouping).length === 1 ? (
              <span className={"empty-space-layout-helper"}></span>
            ) : (
              removeButton("Remove group", () =>
                updateGrouping(a, i, row => ({
                  ...row,
                  qAENonComparativeGroupNames: baseGroupNames(row).filter(
                    (_, index) => index !== gnIndex
                  ),
                }))
              )
            )}
          </div>
        ))}
        {addButton("Add another", () =>
          updateGrouping(a, i, row => ({
            ...row,
            qAENonComparativeGroupNames: [...baseGroupNames(row), ""],
          }))
        )}
      </>,
      number
    );

  const renderTreatmentGroups = (grouping, a, i, number) =>
    renderQuestion(
      "qAETreatmentGroups",
      <>
        {baseTreatmentGroups(grouping).map((tg, tgIndex) => (
          <div
            key={`tg-${a}-${i}-${tgIndex}`}
            className={"questions-content__input-group"}>
            <div className={"form-component"}>
              <label>Name</label>
              <CFormInput
                value={tg.name}
                placeholder={"Treatment group name"}
                onChange={e =>
                  updateGrouping(a, i, row => ({
                    ...row,
                    qAETreatmentGroups: baseTreatmentGroups(row).map(
                      (t, index) =>
                        index === tgIndex ? { ...t, name: e.target.value } : t
                    ),
                  }))
                }
              />
            </div>
            <div className={"form-component"}>
              <label>Role</label>
              <CFormSelect
                value={tg.role}
                options={stringSelectOptions(SCS_TREATMENT_GROUP_ROLE_OPTIONS)}
                onChange={e =>
                  updateGrouping(a, i, row => ({
                    ...row,
                    qAETreatmentGroups: baseTreatmentGroups(row).map(
                      (t, index) =>
                        index === tgIndex ? { ...t, role: e.target.value } : t
                    ),
                  }))
                }
              />
            </div>
            {baseTreatmentGroups(grouping).length === 1 ? (
              <span className={"empty-space-layout-helper"}></span>
            ) : (
              removeButton("Remove treatment group", () =>
                updateGrouping(a, i, row => ({
                  ...row,
                  qAETreatmentGroups: baseTreatmentGroups(row).filter(
                    (_, index) => index !== tgIndex
                  ),
                }))
              )
            )}
          </div>
        ))}
        {addButton("Add Treatment Group", () =>
          updateGrouping(a, i, row => ({
            ...row,
            qAETreatmentGroups: [
              ...baseTreatmentGroups(row),
              emptyTreatmentGroup(),
            ],
          }))
        )}
      </>,
      number
    );

  const renderComparisonPairs = (grouping, a, i, number) => {
    // The Treatment / Comparator dropdowns list the names typed into Q4 for
    // this same grouping (mirrors the Description of Studies Q8 -> Q9 flow).
    const groupNameOptions = stringSelectOptions([
      ...new Set(
        (grouping.qAETreatmentGroups || [])
          .map(tg => (tg?.name || "").trim())
          .filter(Boolean)
      ),
    ]);
    return renderQuestion(
      "qAEComparisonPairs",
      <>
        {basePairs(grouping).map((pair, pairIndex) => (
          <div
            key={`pair-${a}-${i}-${pairIndex}`}
            className={"questions-content__input-group"}>
            <div className={"form-component"}>
              <label>Treatment</label>
              <CFormSelect
                value={pair.treatment}
                options={groupNameOptions}
                onChange={e =>
                  updateGrouping(a, i, row => ({
                    ...row,
                    qAEComparisonPairs: basePairs(row).map((p, index) =>
                      index === pairIndex
                        ? { ...p, treatment: e.target.value }
                        : p
                    ),
                  }))
                }
              />
            </div>
            <div className={"form-component"}>
              <label>Comparator</label>
              <CFormSelect
                value={pair.comparator}
                options={groupNameOptions}
                onChange={e =>
                  updateGrouping(a, i, row => ({
                    ...row,
                    qAEComparisonPairs: basePairs(row).map((p, index) =>
                      index === pairIndex
                        ? { ...p, comparator: e.target.value }
                        : p
                    ),
                  }))
                }
              />
            </div>
            {pairIndex === 0 ? (
              <span className={"empty-space-layout-helper"}></span>
            ) : (
              removeButton("Remove comparison pair", () =>
                updateGrouping(a, i, row => ({
                  ...row,
                  qAEComparisonPairs: basePairs(row).filter(
                    (_, index) => index !== pairIndex
                  ),
                }))
              )
            )}
          </div>
        ))}
        {addButton("Add Comparison Pair", () =>
          updateGrouping(a, i, row => ({
            ...row,
            qAEComparisonPairs: [...basePairs(row), emptyComparisonPair()],
          }))
        )}
      </>,
      number
    );
  };

  const renderDoseGroups = (grouping, a, i, number) =>
    renderQuestion(
      "qAEDoseGroups",
      <>
        {baseDoseGroups(grouping).map((dose, doseIndex) => (
          <div
            key={`dose-${a}-${i}-${doseIndex}`}
            className={"questions-content__input-group"}>
            <div className={"form-component"}>
              <CFormInput
                value={dose}
                placeholder={"Dose group label"}
                onChange={e =>
                  updateGrouping(a, i, row => ({
                    ...row,
                    qAEDoseGroups: baseDoseGroups(row).map((d, index) =>
                      index === doseIndex ? e.target.value : d
                    ),
                  }))
                }
              />
            </div>
            {baseDoseGroups(grouping).length === 1 ? (
              <span className={"empty-space-layout-helper"}></span>
            ) : (
              removeButton("Remove dose group", () =>
                updateGrouping(a, i, row => ({
                  ...row,
                  qAEDoseGroups: baseDoseGroups(row).filter(
                    (_, index) => index !== doseIndex
                  ),
                }))
              )
            )}
          </div>
        ))}
        {addButton("Add Dose Group", () =>
          updateGrouping(a, i, row => ({
            ...row,
            qAEDoseGroups: [...baseDoseGroups(row), ""],
          }))
        )}
      </>,
      number
    );

  // One additional grouping (Q2–Q5) inside a table/analysis section. Groupings
  // beyond the first append "(groupingNumber)" to every question index — the
  // same convention the Description of Studies blocks use (e.g. 2(2), 3(2)).
  const renderGroupingBlock = (grouping, a, i) => {
    const comparisonType = grouping.qAEComparisonType || "";
    const q = n => (i === 0 ? n : `${n}(${i + 1})`);
    // Branch questions are numbered sequentially from 4 in render order, based
    // on the single selected comparison type.
    let branchNumber = 4;
    const nonComparativeGroupNamesNumber =
      comparisonType === "Non-Comparative" ? q(branchNumber++) : null;
    const treatmentGroupsNumber =
      comparisonType === "Comparative" ? q(branchNumber++) : null;
    const comparisonPairsNumber =
      comparisonType === "Comparative" ? q(branchNumber++) : null;
    const doseGroupsNumber =
      comparisonType === "Dose-Finding" ? q(branchNumber++) : null;

    return (
      <div key={`grouping-${a}-${i}`} className={"scs-aae__group"}>
        <div className={"scs-dos__block-actions"}>
          {duplicateBlockButton("Duplicate this group", () =>
            duplicateGrouping(a, i)
          )}
          {i > 0 && removeButton("Remove group", () => removeGrouping(a, i))}
        </div>
        {renderAdditionalGrouping(grouping, a, i, q(2))}
        {renderComparisonType(grouping, a, i, q(3))}
        {comparisonType === "Non-Comparative" &&
          renderNonComparativeGroupNames(
            grouping,
            a,
            i,
            nonComparativeGroupNamesNumber
          )}
        {comparisonType === "Comparative" && (
          <>
            {renderTreatmentGroups(grouping, a, i, treatmentGroupsNumber)}
            {renderComparisonPairs(grouping, a, i, comparisonPairsNumber)}
          </>
        )}
        {comparisonType === "Dose-Finding" &&
          renderDoseGroups(grouping, a, i, doseGroupsNumber)}
      </div>
    );
  };

  // One table/analysis section: Q1 and the additional groupings. With no AE
  // category picked in Q1, nothing below it is shown.
  const renderAnalysisBlock = (analysis, a) => {
    const hasAeTypes = orderedAeTypes(analysis.qAETypes).length > 0;
    return (
      <>
        <div className={"scs-dos__block-actions"}>
          {duplicateBlockButton("Duplicate this table/analysis", () =>
            duplicateAnalysis(a)
          )}
          {analyses.length > MIN_BLOCK_ENTRIES &&
            removeButton("Remove table/analysis", () => removeAnalysis(a))}
        </div>
        {renderAeTypes(analysis, a, 1)}
        {hasAeTypes && (
          <>
            {(analysis.groupings || []).map((grouping, i) =>
              renderGroupingBlock(grouping, a, i)
            )}
            <div className={"scs-dos__block-footer"}>
              {addButton("Add another group", () => addGrouping(a))}
              {duplicateAboveButton("Duplicate group above", () =>
                duplicateGroupingAbove(a)
              )}
            </div>
          </>
        )}
      </>
    );
  };

  // The table/analysis sections are what this page repeats: a single section is
  // rendered on its own, more than one is wrapped in an accordion (the first
  // one expanded on load).
  const renderAnalyses = () => (
    <>
      {analyses.length > 1 && (
        <div className={"scs-dos__accordion-controls"}>
          <CButton
            color={"primary"}
            shape={"pill"}
            className={"scs-dos__duplicate"}
            onClick={() =>
              setOpenAnalysisIndexes(new Set(analyses.map((_, i) => i)))
            }>
            Expand all
          </CButton>
          <CButton
            color={"primary"}
            shape={"pill"}
            className={"scs-dos__duplicate"}
            onClick={() => setOpenAnalysisIndexes(new Set())}>
            Collapse all
          </CButton>
        </div>
      )}
      {analyses.length === 1 ? (
        <div
          ref={el => (analysisRefs.current[0] = el)}
          className={"analysis-block"}>
          {renderAnalysisBlock(analyses[0], 0)}
        </div>
      ) : (
        <div className={"accordion scs-aae__analyses"}>
          {analyses.map((analysis, a) => {
            const isOpen = openAnalysisIndexes.has(a);
            return (
              <div
                key={`analysis-${a}`}
                className={"accordion-item"}
                ref={el => (analysisRefs.current[a] = el)}>
                <div className={"accordion-header"}>
                  <button
                    type={"button"}
                    className={`accordion-button${isOpen ? "" : " collapsed"}`}
                    onClick={() => toggleAnalysis(a)}>
                    {`Table/Analysis ${a + 1}`}
                  </button>
                </div>
                <CCollapse visible={isOpen} className={"accordion-collapse"}>
                  <div className={"accordion-body"}>
                    <div className={"analysis-block"}>
                      {renderAnalysisBlock(analysis, a)}
                    </div>
                  </div>
                </CCollapse>
              </div>
            );
          })}
        </div>
      )}
      <div className={"scs-dos__block-footer"}>
        {addButton("Add another table/analysis", addAnalysis)}
        {duplicateAboveButton(
          "Duplicate another table/analysis",
          duplicateAnalysisAbove
        )}
      </div>
    </>
  );

  // Reuses the `.scs-dos` styles — the block / accordion / duplicate controls
  // are identical to the Description of Studies wizard.
  const PageContent = () => (
    <div className={"questions-page scs-dos scs-aae"}>
      <div className={"questions-content"}>
        <div className={"questions-content__items"}>
          <ToastContainer />
          <form id={FORM_ID} onSubmit={handleSubmit}>
            {renderAnalyses()}
          </form>
          <Tooltip
            id={REMOVE_TOOLTIP_ID}
            place={"top"}
            delayShow={100}
            className={"custom-tooltip"}
          />
          <Tooltip
            id={DUPLICATE_TOOLTIP_ID}
            place={"top"}
            delayShow={100}
            className={"custom-tooltip"}
          />
        </div>
      </div>
    </div>
  );

  if (showRedirect || wrongEnvironment) {
    return <RedirectHandler />;
  }

  return (
    <DashboardContent
      sidebarCSR={true}
      headerTitle={"Analysis of Adverse Events"}
      headerButton={false}
      pageContent={PageContent()}
      prevButton={{
        buttonTitle: "Overall Extent of Exposure",
        buttonGoToAction: `/scs/${summaryId}/overall-extent-of-exposure`,
      }}
      saveButton={true}
      savePermission={[
        PERMISSIONS.scsWizardsRead,
        PERMISSIONS.scsWizardsUpdate,
      ]}
      saveButtonHandler={handleSave}
      saveButtonDisabled={isSubmitting}
      nextButton={{
        buttonTitle: isSubmitting
          ? "Saving..."
          : "Clinical Laboratory Evaluations",
        buttonFormAction: FORM_ID,
        buttonGoToAction: `/scs/${summaryId}/clinical-laboratory-evaluations`,
        disabled: isSubmitting,
      }}
    />
  );
};

export default SCSAnalysisOfAdverseEvents;
